import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { ListAlt } from '@mui/icons-material';
import { useApolloClient, useReactiveVar } from '@apollo/client';
import { FakeText } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import { useQueryWithErrorHandling, useClientQueryWithErrorHandling } from 'customHooks/errorHandling';
import { INSURANCE_SIMULATION_FOR_RCA, LEASING_OFFER_INSURANCE_SIMULATION_IDS, QUOTATIONS_FOR_RCA } from '../../../queries';
import { useChangeTrackingLens, get, set } from '@totalsoft/change-tracking-react';
import RCAInsuranceGeneralData from './RCAInsuranceGeneralData';
import RCAQuotationsByDuration from './RCAQuotationsByDuration';
import insuranceDurations from 'constants/insuranceDurations';
import { emptyArray } from 'constants/emptyConst';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@mui/material';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import {
  INSURANCE_SIMULATION_SAVED,
  QUOTATIONS_SAVED,
  LEASING_SIMULATION_BY_OFFER,
  INSURANCE_OWNER_APPLICATION_CODE_UPDATED,
  RCA_QUOTATION_UPDATED,
  INSURANCE_STATE_CODE_UPDATED_FAILED,
  POLICY_DOCUMENT_NUMBER_SAVED
} from '../../../subscriptions';
import { INSURANCE_SIMULATION_SAVED_EVENT, RCA_QUOTATION_UPDATED_EVENT } from '../../../eventTypes';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { ownInsurance } from 'constants/ownInsurance';
import * as R from 'ramda';
import NegotiationContainer from '../../negotiation/NegotiationContainer';
import { env } from 'utils/env';
import { initialNegotiationState, quotationsStatus } from 'constants/insurance';
import { RightsContext } from 'providers/RightsContext';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { invalidateApolloClientCache } from 'utils/apollo/common';
import CustomBackdrop from 'components/common/backdrop/CustomBackdrop';
import DisclaimerComponent from './DisclaimerComponent';
import useInsuranceParams from '../../../useInsuranceParams';
import {
  commonGeneralDataVar,
  rcaPolicyDocumentNumberVar,
  rcaQuotationsStatusVar,
  rcaSelectedQuotationVar,
  rcaSelectionInProgressVar
} from '../../../variables';

const RCAInsuranceContainer = ({ documentDetail, simulation }) => {
  const [t] = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const client = useApolloClient();
  const clientQuery = useClientQueryWithErrorHandling();
  const addToast = useToast();

  const { readOnlyFull, offerInsuranceWrite } = useContext(RightsContext);
  const { enableWebInsuranceNegotiation } = useContext(ApplicationFeaturesContext);
  const { offerId, offerSiteId } = useInsuranceParams();
  const commonGeneralData = useReactiveVar(commonGeneralDataVar);
  const rcaQuotationsStatus = useReactiveVar(rcaQuotationsStatusVar);
  const selectedQuotation = useReactiveVar(rcaSelectedQuotationVar);
  const selectionInProgress = useReactiveVar(rcaSelectionInProgressVar);
  const policyDocumentNumber = useReactiveVar(rcaPolicyDocumentNumberVar);

  const [expanded, setExpanded] = useState(false);
  const [saving, setSaving] = useState(false);

  const [negotiationLens, , resetNegotiationLens] = useChangeTrackingLens(initialNegotiationState);

  // #region Queries

  const {
    loading: loadingSimulation,
    data: simulationData,
    refetch: refetchSimulation
  } = useQueryWithErrorHandling(INSURANCE_SIMULATION_FOR_RCA, {
    variables: { id: simulation.id, offerId, offerSiteId },
    cacheEviction: true,
    skip: !expanded,
    onCompleted: data => {
      const _simulation = data?.insuranceSimulation;
      rcaSelectedQuotationVar({
        id: _simulation?.selectedInsuranceQuotationId
      });
      rcaQuotationsStatusVar(_simulation?.quotationsStatus);
      rcaPolicyDocumentNumberVar(_simulation?.policyDocumentNumber);
    }
  });

  const {
    loading: loadingQuotations,
    data: quotationsData,
    refetch: refetchQuotations
  } = useQueryWithErrorHandling(QUOTATIONS_FOR_RCA, {
    variables: { insuranceSimulationId: simulation.id },
    cacheEviction: true,
    skip: !expanded
  });

  // #endregion

  const insuranceSimulation = useMemo(() => simulationData?.insuranceSimulation || {}, [simulationData]);
  const quotations = useMemo(() => quotationsData?.quotations || [], [quotationsData]);
  const rcaOwnInsurance = useMemo(() => commonGeneralData.ownRca, [commonGeneralData]);
  const quotationsProcessing = useMemo(() => rcaQuotationsStatus === quotationsStatus.PROCESSING, [rcaQuotationsStatus]);

  const isReadOnly = useCallback(
    isReadOnlyInternally => {
      if (
        readOnlyFull ||
        !offerInsuranceWrite ||
        simulationData?.insuranceSimulation?.ownerApplicationCode === env.REACT_APP_LSNG_INSURANCE_APP_CODE ||
        saving ||
        (negotiationLens.negotiationInProgress |> get) ||
        quotationsProcessing ||
        selectionInProgress ||
        policyDocumentNumber
      ) {
        return true;
      }

      if (isReadOnlyInternally) {
        return isReadOnlyInternally(negotiationLens);
      }

      const negotiationChecked = negotiationLens.negotiationChecked |> get;
      return negotiationChecked;
    },
    [
      policyDocumentNumber,
      readOnlyFull,
      offerInsuranceWrite,
      simulationData?.insuranceSimulation?.ownerApplicationCode,
      saving,
      negotiationLens,
      quotationsProcessing,
      selectionInProgress
    ]
  );

  const onExpand = useCallback((_, expanded) => setExpanded(!expanded), []);

  // #region Side Effects

  useEffect(() => {
    if (quotationsProcessing) {
      onBeginCall();
    }
  }, [onBeginCall, quotationsProcessing]);

  useEffect(() => {
    const negotiationChecked = negotiationLens.negotiationChecked |> get;
    const selectedQuotations = negotiationLens.selectedQuotations |> get;

    if (!negotiationChecked) {
      if (selectedQuotations === emptyArray) return;
      (negotiationLens.selectedQuotations |> set)(emptyArray);
    } else {
      const selectedInsuranceQuotationId = selectedQuotation.id;
      if (!selectedInsuranceQuotationId) return;
      if (R.any(x => x.id === selectedInsuranceQuotationId)(selectedQuotations)) return;
      (negotiationLens.selectedQuotations |> set)([{ id: selectedInsuranceQuotationId }]);
    }
  }, [negotiationLens, quotations, selectedQuotation.id]);

  // #endregion

  // #region Subscriptions

  useGlobalSubscription(LEASING_SIMULATION_BY_OFFER, {
    variables: { offerId, offerSiteId },
    onSubscriptionData: ({
      subscriptionData: {
        data: { quotationSaved }
      }
    }) => {
      if (quotationSaved) {
        refetchSimulation();
      }
    }
  });

  useGlobalSubscription(INSURANCE_SIMULATION_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: async ({
      subscriptionData: {
        data: { insuranceSimulationSaved }
      }
    }) => {
      if (!insuranceSimulationSaved) return;

      if (insuranceSimulationSaved.finishSaving) {
        setSaving(false);
        onEndCall();
      }

      if (!insuranceSimulationSaved.keepQuotations) {
        refetchQuotations();
      }

      if (!insuranceSimulationSaved.silent) addToast(t('Notifications.' + INSURANCE_SIMULATION_SAVED_EVENT), 'info');

      const { data } = await clientQuery(LEASING_OFFER_INSURANCE_SIMULATION_IDS, {
        variables: { offerId, offerSiteId }
      });

      const documentDetails = data?.leasingOffer?.leasingSimulation?.documentDetails || emptyArray;
      documentDetails.forEach(documentDetail => {
        const insuranceSimulations = documentDetail?.insuranceSimulations || emptyArray;
        insuranceSimulations.forEach(insuranceSimulation => {
          if (insuranceSimulation.id === simulation.id) return;

          const insuranceSimulationCacheId = client.cache.identify({
            __typename: 'InsuranceSimulation',
            id: insuranceSimulation.id
          });

          client.cache.evict({ id: insuranceSimulationCacheId, fieldName: 'usageTypeCode' });
        });
      });

      client.cache.gc();
    }
  });

  useGlobalSubscription(RCA_QUOTATION_UPDATED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: _ => addToast(t('Notifications.' + RCA_QUOTATION_UPDATED_EVENT), 'info')
  });

  useGlobalSubscription(QUOTATIONS_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { quotationsSaved }
      }
    }) => {
      if (quotationsSaved) {
        invalidateApolloClientCache(client, { __typename: 'InsuranceSimulation', id: simulation.id }, ['selectedInsuranceQuotationId']);
        refetchQuotations();
      }
    }
  });

  useGlobalSubscription(INSURANCE_OWNER_APPLICATION_CODE_UPDATED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { insuranceOwnerApplicationCodeUpdated }
      }
    }) => {
      if (insuranceOwnerApplicationCodeUpdated?.ownerApplicationCode !== env.REACT_APP_LSNG_INSURANCE_APP_CODE)
        resetNegotiationLens(prevState => ({
          ...prevState,
          negotiationInProgress: false
        }));
    }
  });

  useGlobalSubscription(INSURANCE_STATE_CODE_UPDATED_FAILED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { externalInsuranceStateCodeUpdateFailed }
      }
    }) => {
      setSaving(false);
      onEndCall();
      if (externalInsuranceStateCodeUpdateFailed.message) addToast(externalInsuranceStateCodeUpdateFailed.message, 'error');
    }
  });

  useGlobalSubscription(POLICY_DOCUMENT_NUMBER_SAVED, {
    variables: { insuranceSimulationId: simulation.id },
    onSubscriptionData: ({
      subscriptionData: {
        data: { policyDocumentNumberSaved }
      }
    }) => {
      if (policyDocumentNumberSaved) {
        rcaPolicyDocumentNumberVar(policyDocumentNumberSaved?.policyDocumentNumber);
        addToast(t('Offer.Insurance.Notifications.RCAPolicyDocumentNumberSaved'), 'info');
      }
    }
  });

  // #endregion

  return (
    <>
      <CollapseOrBlankCard
        id={'rcaInsuranceCard'}
        icon={ListAlt}
        iconColor={'info'}
        expanded={expanded}
        canExpand={rcaOwnInsurance === ownInsurance.No}
        onToggle={onExpand}
        title={simulation.insuranceTypeName}
        header={
          <Grid container justifyContent='flex-start' alignItems='baseline' spacing={2}>
            <Typography variant='h6'>{simulation.insuranceTypeName}</Typography>
            {rcaOwnInsurance === ownInsurance.FirstYear || rcaOwnInsurance === ownInsurance.EntirePeriod ? (
              <Grid item>{t('Offer.Insurance.OwnInsuranceSelected')}</Grid>
            ) : (
              rcaOwnInsurance === ownInsurance.NoInsurance && <Grid item>{t('Offer.Insurance.NoInsurance')}</Grid>
            )}
          </Grid>
        }
      >
        {expanded ? (
          loadingSimulation || loadingQuotations ? (
            <FakeText lines={5} />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RCAInsuranceGeneralData
                  data={simulationData}
                  refetchSimulation={refetchSimulation}
                  refetchQuotations={refetchQuotations}
                  documentDetail={documentDetail}
                  saving={saving}
                  setSaving={setSaving}
                  isReadOnly={isReadOnly}
                />
              </Grid>
              {quotations.length > 0 && enableWebInsuranceNegotiation && (
                <Grid item xs={12}>
                  <NegotiationContainer
                    simulation={insuranceSimulation}
                    lens={negotiationLens}
                    resetLens={resetNegotiationLens}
                    isReadOnly={isReadOnly}
                  />
                </Grid>
              )}
              <Grid item container xs={12}>
                {quotations &&
                  insuranceDurations
                    .filter(duration => quotations.map(q => q.period).includes(duration))
                    .map(duration => {
                      const quotationsByDuration = quotations.filter(x => x.period === duration);
                      return <RCAQuotationsByDuration key={duration} quotations={quotationsByDuration} isReadOnly={isReadOnly} />;
                    })}
              </Grid>
              {quotations && quotations.length > 0 && (
                <Grid item container xs={12}>
                  <DisclaimerComponent />
                </Grid>
              )}
              <CustomBackdrop message={t('Offer.Insurance.SelectionInProgress')} open={selectionInProgress}></CustomBackdrop>
            </Grid>
          )
        ) : (
          <></>
        )}
      </CollapseOrBlankCard>
    </>
  );
};

RCAInsuranceContainer.propTypes = {
  documentDetail: PropTypes.object.isRequired,
  simulation: PropTypes.object.isRequired
};

export default RCAInsuranceContainer;
